import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Container, FormControlLabel, IconButton, MenuItem, Paper, Select, Stack, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useApi } from '../../hooks/useApi';
import { FILES_NOTE, POLICY_URL, errorText, policyUrl, rulesEditable } from '../SoftwarePolicy/policyEdit';
import {
  definitionBody, existingRule, inventoryUrl, itemFromPackage, policyRows, policySummary, removeRuleBody, ruleSetBody, searchUrl,
} from './builderLogic';

const list = (v) => (Array.isArray(v) ? v : []);
const MODE_COLOR = { required: 'primary.main', allowed: 'success.main', forbidden: 'error.main' };
const OPTIONS = [['required', 'Required'], ['allowed', 'Allowed'], ['forbidden', 'Forbidden'], ['none', 'Not in policy']];

function MachinesButton({ program }) {
  return (
    <Tooltip title={<Box><b>{program.name}</b>{program.versions.map((v) => <div key={v.version}>{v.version || '(no version)'}: {v.machines.join(', ')}</div>)}</Box>}>
      <Button size="small" sx={{ minWidth: 0, textTransform: 'none' }} aria-label={`Machines with ${program.name}`}>
        {program.machineCount} {program.machineCount === 1 ? 'machine' : 'machines'}
      </Button>
    </Tooltip>
  );
}

// What this rule set says about a program, as a drop down: choosing a value saves it at once.
function PolicySelect({ name, value, disabled, busy, reason, onChange }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Tooltip title={disabled ? reason : ''}>
        <span>
          <Select
            size="small" value={value || 'none'} disabled={disabled || busy} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 150, color: MODE_COLOR[value] }}
            SelectDisplayProps={{ 'aria-label': `Policy for ${name}` }}
          >
            {OPTIONS.map(([v, label]) => <MenuItem key={v} value={v}>{label}</MenuItem>)}
          </Select>
        </span>
      </Tooltip>
      {busy && <CircularProgress size={16} />}
    </Stack>
  );
}

// The software of one rule set: the programs installed on the machines its filter matches, each once and matched to winget where it can be,
// and a search of the whole winget catalog. The In policy column of a program saves its rule in this rule set.
// The filter is the rule set's identity and is not changed here.
function SoftwareBuilderPage() {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const theme = useTheme();
  const [params] = useSearchParams();
  const account = params.get('account') || '';
  const ruleSetId = params.get('ruleset') || '';

  const [policy, setPolicy] = useState({ state: 'idle' });
  const [inventory, setInventory] = useState({ state: 'idle' });
  const [tab, setTab] = useState('policy');
  const [searchText, setSearchText] = useState('');
  const [installedOnly, setInstalledOnly] = useState(true);     // the search lists the installed software, or searches all of winget
  const [results, setResults] = useState({ state: 'idle', packages: [] });
  const [savingKey, setSavingKey] = useState(null);        // the program being saved: nothing else is changed meanwhile
  const [saveError, setSaveError] = useState('');
  const [notice, setNotice] = useState('');

  const ruleSet = policy.state === 'ok' ? list(policy.ruleSets).find((rs) => rs.id === ruleSetId) || null : null;
  const editable = policy.state === 'ok' && rulesEditable(policy);
  const programs = useMemo(() => list(inventory.programs), [inventory]);

  // The rule set first, because its filter decides which machines' software is listed.
  const load = useCallback(async () => {
    if (!account || !ruleSetId) return;
    setPolicy({ state: 'loading' });
    setInventory({ state: 'idle' });
    let data;
    try {
      data = (await apiRef.current.request('GET', policyUrl(account))).data;
      setPolicy({ state: 'ok', ...data });
    } catch (ex) {
      console.error('Failed to load the policy:', ex);
      setPolicy({ state: 'error', error: errorText(ex) });
      return;
    }
    const found = list(data.ruleSets).find((rs) => rs.id === ruleSetId);
    if (!found) return;
    setInventory({ state: 'loading' });
    try {
      const response = await apiRef.current.request('GET', inventoryUrl(account, found.filter));
      setInventory({ state: 'ok', ...response.data });
    } catch (ex) {
      console.error('Failed to load the software list:', ex);
      setInventory({ state: 'error', error: errorText(ex) });
    }
  }, [account, ruleSetId]);

  useEffect(() => { setNotice(''); setSaveError(''); load(); }, [load]);

  // The catalog search, a moment after the typing stops.
  useEffect(() => {
    const text = searchText.trim();
    if (installedOnly || text.length < 2) { setResults({ state: 'idle', packages: [] }); return undefined; }
    let cancelled = false;
    setResults((r) => ({ ...r, state: 'loading' }));
    const timer = setTimeout(() => {
      apiRef.current.request('GET', searchUrl(text))
        .then((r) => { if (!cancelled) setResults({ state: 'ok', packages: list(r.data?.packages) }); })
        .catch((ex) => { if (!cancelled) { console.error('Failed to search winget:', ex); setResults({ state: 'error', packages: [], error: errorText(ex) }); } });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchText, installedOnly]);

  // One table: the winget packages of the installed software (filtered by the text), or what the catalog search found.
  const words = searchText.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const installedPackages = [...new Map(programs.filter((p) => p.winget).map((p) => [p.winget.id, { id: p.winget.id, name: p.winget.name || p.name, latestVersion: p.winget.latestVersion }])).values()]
    .filter((pkg) => words.every((w) => `${pkg.id} ${pkg.name}`.toLowerCase().includes(w)));
  const packages = installedOnly ? installedPackages : results.packages;
  const installedByWinget = (id) => programs.find((p) => p.winget?.id === id || p.definition?.key === id);
  const rows = policyRows(ruleSet, policy.definitions, programs, inventory.machines || 0);
  const summary = policySummary(rows);
  const inventoryReady = inventory.state === 'ok';
  const knownKeys = new Set(list(policy.definitions).map((d) => d.key));

  // Set what this rule set says about a program ('none' takes it out). A program with no definition gets one first. The saved rule set
  // replaces the one on the page, so the row shows the new value at once; a failure leaves the row as it was and says why.
  const setMode = async (item, mode) => {
    setSavingKey(item.key);
    setSaveError('');
    setNotice('');
    try {
      let created = null;
      let shownAs = null;                                  // what the page knows the program as until it is read again (nothing is stored for it)
      let body;
      if (mode === 'none') {
        body = removeRuleBody(account, ruleSet, item.key);
      } else {
        // A winget program needs no stored definition: its winget id is the key and the API works the rest out from the catalog.
        if (!item.definitionExists && !knownKeys.has(item.key) && !item.wingetId) {
          created = definitionBody(account, { ...item, mode });
          await apiRef.current.request('POST', POLICY_URL, created);
        }
        if (!created && !knownKeys.has(item.key) && item.wingetId) shownAs = { key: item.key, displayName: item.name, note: '', detect: { namePattern: null, wingetId: item.wingetId }, actions: {}, builtIn: true };
        body = ruleSetBody(account, ruleSet, { ...item, mode, reason: existingRule(ruleSet, item.key)?.reason || '' });
      }
      const response = await apiRef.current.request('POST', POLICY_URL, body);
      const saved = Array.isArray(response?.data?.rules) ? response.data : body;
      setPolicy((current) => ({
        ...current,
        definitions: created || shownAs ? [...list(current.definitions), created || shownAs] : current.definitions,
        ruleSets: list(current.ruleSets).map((rs) => (rs.id === ruleSetId ? { ...rs, ...saved, id: rs.id } : rs)),
      }));
      setNotice(mode === 'none' ? `${item.name} is no longer in this rule set.` : `${item.name} is ${mode} in this rule set.`);
    } catch (ex) {
      setSaveError(`${item.name}: ${errorText(ex)}`);
    } finally {
      setSavingKey(null);
    }
  };

  const back = `/softwarepolicy${account ? `?account=${encodeURIComponent(account)}` : ''}`;
  const loading = policy.state === 'loading' || inventory.state === 'loading';
  const lockedReason = !editable ? FILES_NOTE : 'Not in winget. Add a definition for it on the Programs tab first.';

  return (
    <Box sx={{ minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="xl">
        <Box sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, background: alpha(theme.palette.background.paper, 0.6),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}><HomeRoundedIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} /></Link>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Link to="/endpointdevices" style={{ textDecoration: 'none' }}><Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Endpoint Devices</Typography></Link>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Link to={back} style={{ textDecoration: 'none' }}><Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Software policy</Typography></Link>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Build your software policy</Typography>
          </Box>
        </Box>

        <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Build your software policy</Typography>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ my: 0.5 }} aria-label="Applies to">
                <Typography variant="body2" color="text.secondary">Applies to</Typography>
                {ruleSet ? list(ruleSet.filter).map((f) => <Chip key={`${f.label}-${f.value}`} size="small" label={`${f.label} is ${f.value}`} />) : <Typography variant="body2">-</Typography>}
                {ruleSet?.description && <Typography variant="body2" color="text.secondary">({ruleSet.description})</Typography>}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Pick the software that matters from what is installed on these machines, or search every winget package. Set each program to required, allowed or forbidden in the In policy column: the change is saved at once.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Reload"><span><IconButton onClick={load} disabled={!ruleSetId || loading} size="small" aria-label="Reload"><RefreshIcon /></IconButton></span></Tooltip>
              <Button component={Link} to={back} startIcon={<ArrowBackIcon />}>Software policy</Button>
            </Stack>
          </Stack>
        </Paper>

        {!account || !ruleSetId ? <Alert severity="info">Choose a rule set on the <Link to={back}>Software policy</Link> page and use its Edit button.</Alert> : null}
        {saveError && <Alert severity="error" onClose={() => setSaveError('')} sx={{ mb: 2 }}>{saveError}</Alert>}
        {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}
        {policy.state === 'ok' && !editable && <Alert severity="info" sx={{ mb: 2 }}>{FILES_NOTE}</Alert>}
        {policy.state === 'error' && <Alert severity="error" sx={{ mb: 2 }}>{policy.error}</Alert>}
        {policy.state === 'ok' && !ruleSet && <Alert severity="warning">This rule set was not found. It may have been deleted: go back to the <Link to={back}>Software policy</Link> page.</Alert>}

        {ruleSet && (
          <Paper sx={{ borderRadius: 2 }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label="Where to find software" sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
              <Tab value="policy" label={`Policy (${rows.length})`} />
              <Tab value="search" label="Search winget packages" />
            </Tabs>

            <Box sx={{ p: 2 }}>
              {tab === 'policy' && (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    What this rule set says today. Change a program in the In policy column, or find more software on the Search tab.
                  </Typography>
                  {inventory.state === 'loading' && <CircularProgress size={20} />}
                  {inventory.state === 'error' && <Alert severity="warning">{`The machines' software could not be read, so what the rules find today is not shown. ${inventory.error}`}</Alert>}
                  {rows.length === 0 ? (
                    <Alert severity="info">This rule set has no rules yet. Use the Search tab to choose software for it.</Alert>
                  ) : (
                    <>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                        <Chip size="small" label={`${summary.rules} ${summary.rules === 1 ? 'rule' : 'rules'}`} />
                        <Chip size="small" variant="outlined" label={`${summary.required} required`} />
                        <Chip size="small" variant="outlined" label={`${summary.allowed} allowed`} />
                        <Chip size="small" variant="outlined" label={`${summary.forbidden} forbidden`} />
                        {inventoryReady && <Chip size="small" color={summary.missing + summary.present + summary.outdated ? 'warning' : 'success'} variant="outlined"
                          label={`Today: ${summary.missing} missing, ${summary.present} forbidden present, ${summary.outdated} to update`} />}
                      </Stack>
                      <TableContainer>
                        <Table size="small" aria-label="Policy">
                          <TableHead>
                            <TableRow>{['Program', 'In policy', 'Machines', 'Today', 'winget'].map((h) => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
                          </TableHead>
                          <TableBody>
                            {rows.map((r) => (
                              <TableRow key={r.key} hover sx={{ verticalAlign: 'top', opacity: r.enabled ? 1 : 0.55 }}>
                                <TableCell>{r.name}<Typography variant="caption" color="text.secondary" display="block">{r.key}</Typography></TableCell>
                                <TableCell>
                                  <PolicySelect name={r.name} value={r.mode} busy={savingKey === r.key} disabled={!editable || savingKey !== null} reason={lockedReason}
                                    onChange={(value) => setMode({ key: r.key, name: r.name, definitionExists: true }, value)} />
                                </TableCell>
                                <TableCell>{!inventoryReady ? '-' : r.program ? <MachinesButton program={r.program} /> : <Typography variant="caption" color="text.secondary">not installed</Typography>}</TableCell>
                                <TableCell>
                                  <Stack spacing={0.5} alignItems="flex-start">
                                    {r.missing > 0 && inventoryReady && <Chip size="small" color="error" label={`${r.missing} missing`} />}
                                    {r.present > 0 && <Chip size="small" color="error" label={`${r.present} to remove`} />}
                                    {r.outdated > 0 && <Chip size="small" color="warning" label={`${r.outdated} to update`} />}
                                    {inventoryReady && !r.missing && !r.present && !r.outdated && <Typography variant="caption" color="text.secondary">in line</Typography>}
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  {r.wingetId ? <Chip size="small" color="success" variant="outlined" label={r.wingetId} /> : <Typography variant="caption" color="text.secondary">not in winget</Typography>}
                                  {r.program?.winget && <Typography variant="caption" color="text.secondary" display="block">newest {r.program.winget.latestVersion}</Typography>}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </Stack>
              )}

              {tab === 'search' && (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    {installedOnly
                      ? 'The winget packages installed on the machines of this rule set. Clear the box to search all of Microsoft\'s winget catalog.'
                      : 'Search Microsoft\'s winget catalog for a program, for example one that is not installed yet that you want to make required on every machine.'}
                  </Typography>
                  <TextField size="small" label="Search winget" value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Name, id or keyword, for example storage explorer" fullWidth />
                  <FormControlLabel control={<Checkbox checked={installedOnly} onChange={(e) => setInstalledOnly(e.target.checked)} />} label="Only software installed on these machines" />
                  {(installedOnly ? inventory.state === 'loading' : results.state === 'loading') && <CircularProgress size={20} />}
                  {installedOnly && inventory.state === 'error' && <Alert severity="error">{inventory.error}</Alert>}
                  {!installedOnly && results.state === 'error' && <Alert severity="error">{results.error}</Alert>}
                  {!installedOnly && results.state === 'idle' && <Typography variant="body2" color="text.secondary">Type at least two characters to search.</Typography>}
                  {installedOnly && inventoryReady && packages.length === 0 && <Typography variant="body2" color="text.secondary">No installed winget package matches.</Typography>}
                  {!installedOnly && results.state === 'ok' && packages.length === 0 && <Typography variant="body2" color="text.secondary">{`No winget package matches "${searchText.trim()}".`}</Typography>}
                  {packages.length > 0 && (
                    <TableContainer>
                      <Table size="small" aria-label="winget packages">
                        <TableHead><TableRow>{['Package', 'winget id', 'Newest', 'On the machines', 'In policy'].map((h) => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                        <TableBody>
                          {packages.map((pkg) => {
                            const installed = installedByWinget(pkg.id);
                            const key = installed?.definition?.key || pkg.id;
                            const rule = existingRule(ruleSet, key);
                            return (
                              <TableRow key={pkg.id} hover selected={Boolean(rule)}>
                                <TableCell sx={{ fontWeight: 600 }}>{pkg.name}</TableCell>
                                <TableCell><Chip size="small" color="success" variant="outlined" label={pkg.id} /></TableCell>
                                <TableCell>{pkg.latestVersion}</TableCell>
                                <TableCell>{installed ? <MachinesButton program={installed} /> : <Typography variant="caption" color="text.secondary">not found</Typography>}</TableCell>
                                <TableCell>
                                  <PolicySelect name={pkg.name} value={rule?.mode} busy={savingKey === key} disabled={!editable || savingKey !== null} reason={lockedReason}
                                    onChange={(value) => setMode(itemFromPackage(pkg, installed), value)} />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  <Alert severity="info" variant="outlined">The catalog is refreshed every night from Microsoft. The "Installed name matches" pattern is suggested from the package name and can be adjusted on the Programs tab.</Alert>
                </Stack>
              )}
            </Box>
          </Paper>
        )}
      </Container>

    </Box>
  );
}

export default SoftwareBuilderPage;
